import { AxiosResponse } from 'axios';
import React from 'react';
import { ReactNode } from 'react';

export {};

export class Optional<T> {
	readonly t: T[] = [];

	private constructor(instance?: T) {
		if (instance !== undefined) {
			this.t.push(instance);
		}
	}

	public hasValue() {
		return !this.t.isEmpty();
	}

	public static WrapToJust = <B>(e: B, f: () => Promise<void>) => {
		return async () => {
			await f();
			return Optional.Just(e);
		};
	};

	public static Maybe = <B>(b: B | null | undefined): Optional<B> => {
		if (b) return this.Just(b);
		return this.None();
	};

	public static Just = <B>(b: B): Optional<B> => {
		return new Optional(b);
	};

	static Empty = new Optional();

	public static None = <B>(): Optional<B> => {
		return Optional.Empty as any;
	};

	public zip<B>(o: Optional<B>): Optional<[T, B]> {
		if (this.hasValue() && o.hasValue()) {
			return Optional.Just([this.get(), o.get()]);
		}
		return Optional.None();
	}

	public map<B>(f: (t: T) => B): Optional<B> {
		if (this.t.isEmpty()) return Optional.None();
		return Optional.Just(f(this.t[0]));
	}

	public keep<B>(f: (t: T) => B | null): Optional<B> {
		if (this.t.isEmpty()) return Optional.None();
		const result = f(this.t[0]);
		if (result) return Optional.Just(result);
		return Optional.None();
	}

	public log(message?: string): Optional<T> {
		if (this.t.isEmpty()) return Optional.None<T>();

		Optional.Maybe(message)
			.do((e) => console.info(e, this.t))
			.orElseDo(() => {
				console.info(this.t);
			});
		return this;
	}

	public toNode(f: (t: T) => ReactNode): ReactNode {
		if (this.t.isEmpty()) return React.createElement('div');
		return f(this.t[0]);
	}

	public flatMap<B>(f: (t: T) => B[]): B[] {
		if (this.t.isEmpty()) return [];
		return f(this.t[0]);
	}

	public async mapAsync<B>(f: (t: T) => Promise<Optional<B>>): Promise<Optional<B>> {
		if (this.t.isEmpty()) return Optional.None();
		return await f(this.t[0]);
	}

	public filter(f: (t: T) => boolean): Optional<T> {
		if (this.t.isEmpty()) return Optional.None();
		var filtered = this.t.filter(f);
		if (filtered.isEmpty()) return Optional.None();

		return this;
	}

	public do(f: (t: T) => void): Optional<T> {
		if (this.t.isEmpty()) return this;
		f(this.t[0]);
		return this;
	}

	public tryParseJson<K>(): Optional<K> {
		if (this.t.isEmpty()) return Optional.Empty as any;

		try {
			const data = this.get() as string;
			const unescapedData = data.replace(/^"(.*)"$/, '$1').replace(/\\"/g, '"');

			return Optional.Just(JSON.parse(unescapedData));
		} catch (e) {
			return Optional.Empty as any;
		}
	}

	public async doAsync(f: (t: T) => Promise<any>): Promise<Optional<T>> {
		if (this.t.isEmpty()) return this;
		await f(this.t[0]);
		return this;
	}

	public or(o: Optional<T>): Optional<T> {
		if (!this.t.length) return o;
		return this;
	}

	public orElse(f: () => T): T {
		if (this.t.isEmpty()) return f();
		return this.t[0];
	}
	public async orElseAsyncOptional(f: () => Promise<Optional<T>>): Promise<Optional<T>> {
		if (this.t.isEmpty()) return await f();
		return this;
	}
	public async orElseAsync(f: () => Promise<T>): Promise<T> {
		if (this.t.isEmpty()) return await f();
		return this.t[0];
	}

	public orElseDo(f: () => void): Optional<T> {
		if (this.t.isEmpty()) {
			f();
			return this;
		}
		return this;
	}

	public get(): T {
		if (this.t.isEmpty()) {
			throw 'Attempt blocked to force a default value without providing it explicitly.';
		}
		return this.t[0];
	}
	public getOrDefault(defaultValue: T): T {
		if (this.t.isEmpty()) {
			return defaultValue;
		}
		return this.t[0];
	}

	public toNullable(): T | null {
		if (this.t.isEmpty()) {
			return null;
		}
		return this.t[0];
	}

	public toArray<B>(f: (t: T) => B) {
		return this.t.map(f);
	}
}

declare global {
	interface Promise<T> {
		/**
		 * Converts a promise into an awaitable optional. Any error returns Optional.None
		 * This is because our middleware handles all errors and we cannot return anything meaningful
		 * to a caller in case of an error. The generic error will still be displayed, but all handlers
		 * calling the API will need to act based on the result of the api call using optional.do(x => ...)
		 */
		toOptional(): Promise<Optional<T>>;

		/**
		 * Like toOptional, but additionally gives you the possibilty to map the incoming request (if there's no error)
		 * @param f
		 */
		toOptionalMapped<B>(f: (t: T) => B): Promise<Optional<B>>;

		/**
		 * Like toOptional, but hardcodes the result type of being Optionan<void>.
		 */
		toOptionalVoid(): Promise<Optional<void>>;

		/**
		 * Wraps the result of the promise in an optional when it passes the condition.
		 *
		 * @param f
		 */
		toOptionalFiltered(f: (t: T) => boolean): Promise<Optional<T>>;

		/**
		 * Conceptually the same as the .then() method. TODO: consider removal. (I think the original idea was to pass in an async function,
		 * not a regular function)
		 * @param f
		 */
		mapAsync<B>(f: (t: T) => B): Promise<B>;

		/**
		 * This should only be applied if the promise return type is Optional<AxiosResponse>
		 * Unlike toOptionalMapped which we can use to map an AxiosResponse into a type B, this
		 * method is intended to be applied on an Optional<AxiosResponse>, because in some cases
		 * we must first check if the AxiosRespose is even mappable to output type B.
		 *
		 * @param f
		 */
		mapAxiosResponse<B>(f: (t: AxiosResponse<any, any>) => B): Promise<Optional<B>>;
		map<B>(f: (t: T) => B): Promise<B>;
		mapAsync<B>(f: (t: T) => Promise<B>): Promise<B>;
	}
}

Promise.prototype.toOptional = async function <T>(): Promise<Optional<T>> {
	try {
		const t: T = await this;
		return Optional.Just(t);
	} catch (e) {
		return Optional.None();
	}
};

Promise.prototype.toOptionalVoid = async function (): Promise<Optional<void>> {
	const response = await this.toOptional();
	return response.map((_) => {});
};

Promise.prototype.toOptionalMapped = async function <T, B>(f: (t: T) => B): Promise<Optional<B>> {
	const response = await this.toOptional();
	return response.map((t) => f(t));
};

Promise.prototype.map = async function <T, B>(f: (t: T) => B): Promise<B> {
	const t = await this;
	return f(t);
};

Promise.prototype.toOptionalFiltered = async function <T>(
	f: (t: T) => boolean
): Promise<Optional<T>> {
	const response = await this.toOptional();
	response.filter(f);
	return response;
	// return response.filter(f);
};

Promise.prototype.mapAxiosResponse = async function <B>(
	f: (t: AxiosResponse<any, any>) => B
): Promise<Optional<B>> {
	const response: Optional<AxiosResponse<any, any>> = await this;
	return response.map(f);
};

Promise.prototype.mapAsync = async function <T, B>(f: (t: T) => B): Promise<B> {
	const t = await this;
	return f(t);
};
